import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { JudgementModal, StartGate, FailOverlay, MinigameHud, StatusMeter } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

const Template12Interrogation = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_interrogation_12', templateId = 12 }) => {
  const defaultConfig = {
    title: '尋問',
    suspect: {
      name: '容疑者',
      avatar: '👤',
      initialTrust: 30,
      initialStress: 50,
      initialDefense: 50
    },
    phases: [
      {
        id: 'phase1',
        name: '導入',
        dialogue: [
          { speaker: 'detective', text: '少し話を聞かせてもらえますか？', emotion: 'neutral' },
          { speaker: 'suspect', text: '...何の用ですか？', emotion: 'guarded' }
        ],
        options: [
          {
            text: '協力的に話を聞く',
            effect: { trust: 25, stress: -10, defense: -15 },
            response: 'まあ...少しなら。あなたなら信用できそうだ。',
            flag: 'cooperative'
          },
          {
            text: '圧力をかける',
            effect: { trust: -15, stress: 25 },
            response: 'な、何なんですか急に...！',
            flag: 'pressure'
          },
          {
            text: '証拠を見せる',
            effect: { trust: 5, stress: 15, defense: -10 },
            response: 'それは...どこで...',
            flag: 'evidence'
          }
        ]
      }
    ],
    confessionThreshold: 70,
    breakdownThreshold: 90
  };

  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!next.suspect || !next.suspect.name) {
      next.suspect = defaultConfig.suspect;
    }
    if (!Array.isArray(next.phases) || next.phases.length === 0) {
      next.phases = defaultConfig.phases;
    }
    return next;
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  const [trust, setTrust] = useState(resolvedConfig.suspect.initialTrust ?? 30);
  const [stress, setStress] = useState(resolvedConfig.suspect.initialStress ?? 50);
  const [defense, setDefense] = useState(resolvedConfig.suspect.initialDefense ?? 50);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [dialogueIndex, setDialogueIndex] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const [response, setResponse] = useState(null);
  const [gameState, setGameState] = useState('playing');
  const [flags, setFlags] = useState([]);
  const [emotion, setEmotion] = useState('neutral');
  const [showEffect, setShowEffect] = useState(null);
  // R1 体験文法: 開始ゲート（hostMeta.ui.showTutorial !== false で表示）と retryCount
  const [gateOpen, setGateOpen] = useState(hostMeta?.ui?.showTutorial !== false);
  const [retryCount, setRetryCount] = useState(0);

  // R1 実動タイマー: T12 の config に固有タイマーは無いため hostMeta.timeLimitMs のみ
  // （0 は「未設定」）。設定時のみ HUD に表示し、満了で失敗にする。
  const timeLimitMs =
    typeof hostMeta?.timeLimitMs === 'number' && hostMeta.timeLimitMs > 0
      ? hostMeta.timeLimitMs
      : null;
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: gameState === 'playing' && !gateOpen,
    onExpire: () => setGameState('fail')
  });

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setTrust(resolvedConfig.suspect.initialTrust ?? 30);
    setStress(resolvedConfig.suspect.initialStress ?? 50);
    setDefense(resolvedConfig.suspect.initialDefense ?? 50);
    setCurrentPhaseIndex(0);
    setDialogueIndex(0);
    setShowOptions(false);
    setResponse(null);
    setGameState('playing');
    setFlags([]);
    setEmotion('neutral');
    resetSubmission();
  }, [resetSubmission, resolvedConfig]);

  const currentPhase = resolvedConfig.phases[currentPhaseIndex];
  const currentDialogue = currentPhase?.dialogue?.[dialogueIndex];

  useEffect(() => {
    if (gameState !== 'playing') return;
    if (trust >= resolvedConfig.confessionThreshold && defense <= 20) {
      setShowEffect('success');
      setTimeout(() => setGameState('confession'), 800);
    } else if (stress >= resolvedConfig.breakdownThreshold) {
      setShowEffect('success');
      setTimeout(() => setGameState('breakdown'), 800);
    }
  }, [trust, stress, defense, gameState, resolvedConfig]);

  useEffect(() => {
    if (gameState === 'fail') {
      setShowEffect('fail');
    }
  }, [gameState]);

  const advanceDialogue = () => {
    if (response) {
      setResponse(null);
      if (currentPhaseIndex < resolvedConfig.phases.length - 1) {
        setCurrentPhaseIndex(prev => prev + 1);
        setDialogueIndex(0);
        setShowOptions(false);
      } else {
        if (trust >= 40) {
          setGameState('confession');
        } else {
          setGameState('fail');
        }
      }
      return;
    }

    if (dialogueIndex < (currentPhase?.dialogue?.length ?? 0) - 1) {
      setDialogueIndex(prev => prev + 1);
      const nextDialogue = currentPhase?.dialogue?.[dialogueIndex + 1];
      if (nextDialogue?.emotion) {
        setEmotion(nextDialogue.emotion);
      }
    } else {
      setShowOptions(true);
    }
  };

  const selectOption = (option) => {
    if (option.effect) {
      if (option.effect.trust) setTrust(prev => Math.max(0, Math.min(100, prev + option.effect.trust)));
      if (option.effect.stress) setStress(prev => Math.max(0, Math.min(100, prev + option.effect.stress)));
      if (option.effect.defense) setDefense(prev => Math.max(0, Math.min(100, prev + option.effect.defense)));
    }
    if (option.flag) {
      setFlags(prev => [...prev, option.flag]);
    }
    setResponse(option.response);
    setShowOptions(false);
  };

  const getEmotionEmoji = () => {
    switch (emotion) {
      case 'nervous': return '😰';
      case 'angry': return '😠';
      case 'sad': return '😢';
      case 'guarded': return '😐';
      case 'scared': return '😨';
      case 'relieved': return '😌';
      default: return resolvedConfig.suspect.avatar ?? '👤';
    }
  };

  const reset = () => {
    setTrust(resolvedConfig.suspect.initialTrust ?? 30);
    setStress(resolvedConfig.suspect.initialStress ?? 50);
    setDefense(resolvedConfig.suspect.initialDefense ?? 50);
    setCurrentPhaseIndex(0);
    setDialogueIndex(0);
    setShowOptions(false);
    setResponse(null);
    setGameState('playing');
    setFlags([]);
    setShowEffect(null);
    setEmotion('neutral');
    // R1: やり直しは retryCount に積算し（全 submit payload に同梱）、タイマーも計り直す
    setRetryCount((prev) => prev + 1);
    resetTimer();
    resetSubmission();
  };

  const title = resolvedConfig.title ?? '尋問';

  return (
    <div className="t12-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {/* R1: 独自の上部固定 HUD バー（タイトル＋3メーター）のタイトル部を MinigameHud semantic へ置換。
          timer は hostMeta 設定時のみ表示。? ボタンでゲート再表示。 */}
      <MinigameHud
        title={title}
        icon="MessageCircleQuestion"
        timer={remainingMs != null && timeLimitMs != null ? { remainingMs, totalMs: timeLimitMs } : null}
        onHelp={() => setGateOpen(true)}
      />

      <div className="absolute inset-0 flex flex-col pt-12 pb-4 px-4">
        {/* R1: 信頼/ストレス/防御の独自3メーターを StatusMeter semantic へ置換。
            意味色は variant で対応（信頼=positive/success・ストレス=negative/error・防御=neutral/accent）。
            値の clamp・自白/崩壊の閾値ロジックは不変（見た目だけ共通文法へ）。 */}
        <StatusMeter
          className="t12-meters w-full max-w-md mx-auto rounded-xl p-3"
          meters={[
            { id: 'trust', label: '信頼', current: trust, type: 'positive' },
            { id: 'stress', label: 'ストレス', current: stress, type: 'negative' },
            { id: 'defense', label: '防御', current: defense, type: 'neutral' }
          ]}
          showValue
        />

        <div className="flex-1 min-h-0 flex flex-col justify-center items-center">
          <div className="text-6xl mb-4 animate-pulse">
            {getEmotionEmoji()}
          </div>
          <div className="t12-suspect-name font-bold mb-4">{resolvedConfig.suspect.name}</div>

          {gameState === 'playing' && (
            <div className="w-full max-w-md">
              {!showOptions ? (
                <button
                  className="t12-bubble w-full backdrop-blur rounded-xl p-4 text-left"
                  onClick={advanceDialogue}
                >
                  <div className="t12-bubble-speaker text-xs mb-1">
                    {response ? resolvedConfig.suspect.name : (currentDialogue?.speaker === 'detective' ? '探偵' : resolvedConfig.suspect.name)}
                  </div>
                  <div className="t12-bubble-text">
                    {response ?? currentDialogue?.text ?? '...'}
                  </div>
                  <div className="t12-bubble-hint text-xs mt-2 text-right">タップで続ける</div>
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="t12-options-label text-xs mb-1">次の言葉を選ぶ</div>
                  {currentPhase?.options?.map((option, idx) => (
                    <button
                      key={idx}
                      className="t12-option w-full backdrop-blur rounded-xl p-3 text-left transition-all hover:scale-[1.02]"
                      onClick={() => selectOption(option)}
                    >
                      <div className="t12-option-text text-sm">{option.text}</div>
                      <div className="flex gap-2 mt-1">
                        {option.effect?.trust && (
                          <span className={`text-xs ${option.effect.trust > 0 ? 't12-effect--good' : 't12-effect--bad'}`}>
                            信頼{option.effect.trust > 0 ? '+' : ''}{option.effect.trust}
                          </span>
                        )}
                        {option.effect?.stress && (
                          <span className={`text-xs ${option.effect.stress < 0 ? 't12-effect--good' : 't12-effect--bad'}`}>
                            ストレス{option.effect.stress > 0 ? '+' : ''}{option.effect.stress}
                          </span>
                        )}
                        {option.effect?.defense && (
                          <span className={`text-xs ${option.effect.defense < 0 ? 't12-effect--good' : 't12-effect--accent'}`}>
                            防御{option.effect.defense > 0 ? '+' : ''}{option.effect.defense}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Foundation: 自白成功モーダル（中央バッジ🎉 + タイトル + 説明 + 完了 を JudgementModal に集約。
          submit('success', { flags, trust, stress, defense }) の呼び出し・ペイロードは不変
          （R1 で retryCount を同梱）。T12 は lucide 不使用のため絵文字を icon prop で温存。 */}
      <JudgementModal
        open={gameState === 'confession'}
        type="success"
        title="自白を引き出した！"
        description={`${resolvedConfig.suspect.name}は真実を語り始めた`}
        icon={<span className="text-5xl">🎉</span>}
        buttonLabel="完了"
        onSubmit={() => submit('success', { flags, trust, stress, defense, retryCount })}
      />

      {/* Foundation: 精神崩壊成功モーダル（同じく成功扱いの単一進行ボタン型のため JudgementModal に集約。
          submit('success', { ..., breakdown: true }) の呼び出し・ペイロードは不変（R1 で retryCount を同梱）。 */}
      <JudgementModal
        open={gameState === 'breakdown'}
        type="success"
        title="精神的に崩壊"
        description={`${resolvedConfig.suspect.name}は耐えきれなくなった`}
        icon={<span className="text-5xl">😱</span>}
        buttonLabel="完了"
        onSubmit={() => submit('success', { flags, trust, stress, defense, breakdown: true, retryCount })}
      />

      {/* R1: 独自 fail オーバーレイ（やり直す / 失敗として送信）を FailOverlay semantic へ移行。
          retry=reset（retryCount 加算）、proceed=submit('fail')（文言は共通既定「未整理のまま進む」） */}
      {gameState === 'fail' && (
        <FailOverlay
          title="尋問失敗"
          subtitle="情報を引き出せなかった"
          onRetry={reset}
          onProceed={() => submit('fail', { flags, trust, stress, defense, retryCount })}
        />
      )}

      {/* R1 体験文法: 開始ゲート（タイトル＋ルール一行＋始める）。? ボタンで再表示できる */}
      {gateOpen && (
        <StartGate
          title={title}
          instruction="言葉を選び、相手の心を開かせろ"
          icon="MessageCircleQuestion"
          onStart={() => setGateOpen(false)}
        />
      )}

      {showEffect === 'success' && (
        <SuccessEffect type="star" onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'fail' && (
        <FailureEffect type="shake" onComplete={() => setShowEffect(null)} />
      )}
    </div>
  );
};

export default Template12Interrogation;
